import { v4 as uuid } from "uuid";
import Markdown from "react-markdown";
import { Card } from "primereact/card";
import { SafeReportResponse } from "../Conversion";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import styles from "./AttachmentCard.module.css";
import {
  attachmentsUiTypeMap,
  effectiveMapping,
  parseUploadedAt,
} from "./mapping";
import { computeAttachmentRelevance } from "./relevance";
import { useCallback, useContext, useState } from "react";
import { Button } from "primereact/button";
import {
  useAdditionalFiles,
  useDeleteAttachment,
  useThisReportIsUpdating,
  useUpdateReport,
  useUploadAdditionalFiles,
} from "../data";
import { confirmDialog } from "primereact/confirmdialog";
import { EnumEditor } from "../sov/CellEditors";
import { cloneDeep, isEqual } from "lodash";
import { Tooltip } from "primereact/tooltip";
import { flaggedHighlightColor } from "../dashboard/util";
import { ThemeContext } from "../../../themes/Theme";
import * as client from "../../../client";
import {
  downloadAdditionalFile,
  downloadReportFile,
  previewEml,
} from "../../../api/routes";
import { apiErrorDetail } from "../../../api/errors";
import { FileTooLargeError } from "../../../api/uploadBatches";
import { downloadBlob } from "../../../util";
import { FileUpload } from "primereact/fileupload";
import { Message } from "primereact/message";
import { useClientId } from "../../../ClientConfig";
import { ClientId, perClient } from "../../../client-features";
import { Dialog } from "primereact/dialog";
import { useToast } from "../../../context-providers/Toast";
import { useRerunningMessage } from "../RerunUI";
import React from "react";
import { DocumentPreviewModal, canPreview } from "./DocumentPreviewModal";
import { CommentContainer } from "../../../components/comments/Comment";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";

type AttachmentCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
};
type AttachmentType = client.Attachment["original_mapping"];

const attachmentTypeFromString = (
  attachmentText: string
): AttachmentType | undefined => {
  switch (attachmentText) {
    case "Acord App":
      return "acord_app";
    case "Loss Run":
      return "loss_run";
    case "Sov Spreadsheet":
      return "sov_spreadsheet";
    case "Image":
      return "screenshot";
    case "Rater":
      return "rater";
    case "Loss Control Report":
      return "loss_control_report";
    case "Policy Document":
      return "policy_document";
    case "Manuscript Form":
      return "manuscript_form";
    case "Final Manuscript Form":
      return "final_manuscript_form";
    case "Specs Sheet":
      return "specs_sheet";
    case "None":
      return "none";
    case "Record Keeping":
      return "record_keeping";
    case "Luminor Quote":
      return "luminor_quote";
    case "Luminor TRIA":
      return "luminor_tria";
    case "Luminor Binder":
      return "luminor_binder";
    case "Luminor Policy":
      return "luminor_policy";
    case "Lead Quote":
      return "lead_quote";
    case "Lead Binder":
      return "lead_binder";
    case "Lead Policy":
      return "lead_policy";
    case "Bound SOV":
      return "bound_sov";
    case "SL Letter":
      return "sl_letter";
    case "Endorsement File":
      return "endorsement_file";
    // "Endorsement SOV" is deliberately absent: it is a generated
    // classification, never a manual one.
    case "Engineering Report":
      return "engineering_report";
    case "Rate Matrix":
      return "rate_matrix";
    case "Form Review":
      return "form_review";
  }
  return undefined;
};

export const VALID_PDF_TYPES_PER_Client = perClient<AttachmentType[]>({
  pantheon: ["acord_app", "loss_run", "none", "specs_sheet", "record_keeping"],
  luminor: [
    "acord_app",
    "loss_run",
    "none",
    "policy_document",
    "manuscript_form",
    "final_manuscript_form",
    "specs_sheet",
    "record_keeping",
    "luminor_quote",
    "luminor_tria",
    "luminor_binder",
    "luminor_policy",
    "lead_quote",
    "lead_binder",
    "lead_policy",
    "sl_letter",
    "endorsement_file",
    "engineering_report",
    "rate_matrix",
    "form_review",
  ],
});

export const VALID_XLSX_TYPES_PER_Client = perClient<AttachmentType[]>({
  pantheon: [
    "loss_run",
    "sov_spreadsheet",
    "bound_sov",
    "specs_sheet",
    "none",
    "record_keeping",
  ],
  luminor: [
    "loss_run",
    "sov_spreadsheet",
    "bound_sov",
    "specs_sheet",
    "none",
    "record_keeping",
    "endorsement_file",
    "rate_matrix",
  ],
});

export const VALID_DOC_TYPES_PER_Client = perClient<AttachmentType[]>({
  pantheon: ["loss_run", "specs_sheet", "none", "record_keeping"],
  luminor: [
    "loss_run",
    "policy_document",
    "manuscript_form",
    "final_manuscript_form",
    "specs_sheet",
    "none",
    "record_keeping",
    "luminor_quote",
    "luminor_tria",
    "luminor_binder",
    "luminor_policy",
    "lead_quote",
    "lead_binder",
    "lead_policy",
    "sl_letter",
    "endorsement_file",
    "engineering_report",
    "rate_matrix",
    "form_review",
  ],
});

export const validFileTypesForFilename = (
  filename: string,
  clientId: ClientId
): AttachmentType[] => {
  const filenameLower = filename.toLowerCase();
  if (filenameLower.endsWith(".pdf")) {
    return VALID_PDF_TYPES_PER_Client[clientId] || [];
  }
  if (
    filenameLower.endsWith(".xlsx") ||
    filenameLower.endsWith(".xls") ||
    filenameLower.endsWith(".xlsm") ||
    filenameLower.endsWith(".xlsb") ||
    filenameLower.endsWith(".csv")
  ) {
    return VALID_XLSX_TYPES_PER_Client[clientId] || [];
  }
  if (
    filenameLower.endsWith(".docx") ||
    filenameLower.endsWith(".doc") ||
    filenameLower.endsWith(".docm")
  ) {
    return VALID_DOC_TYPES_PER_Client[clientId] || [];
  }

  if (
    filenameLower.endsWith(".png") ||
    filenameLower.endsWith(".jpg") ||
    filenameLower.endsWith(".jpeg")
  ) {
    return ["screenshot", "none"];
  }

  return ["none"];
};

export const iconForFilename = (filename: string) => {
  const filenameLower = filename.toLowerCase();
  if (filenameLower.endsWith(".pdf")) {
    return "pi pi-file-pdf";
  }
  if (
    filenameLower.endsWith(".xlsx") ||
    filenameLower.endsWith(".xls") ||
    filenameLower.endsWith(".xlsm") ||
    filenameLower.endsWith(".xlsb") ||
    filenameLower.endsWith(".csv")
  ) {
    return "pi pi-file-excel";
  }
  if (
    filenameLower.endsWith(".docx") ||
    filenameLower.endsWith(".doc") ||
    filenameLower.endsWith(".docm")
  ) {
    return "pi pi-file-word";
  }
  if (
    filenameLower.endsWith(".png") ||
    filenameLower.endsWith(".jpg") ||
    filenameLower.endsWith(".jpeg")
  ) {
    return "pi pi-image";
  }

  return "pi pi-file";
};

const splitFilename = (filename: string): { base: string; ext: string } => {
  const dotIndex = filename.lastIndexOf(".");
  // A leading dot (e.g. ".gitignore") is part of the base, not an extension.
  if (dotIndex > 0) {
    return { base: filename.slice(0, dotIndex), ext: filename.slice(dotIndex) };
  }
  return { base: filename, ext: "" };
};

// Mirrors the backend's `_attachment_name_key`: collapse whitespace runs to a
// single space, trim, and lowercase. Comparing on this key means the frontend
// flags exactly the conflicts the upload endpoint would reject with HTTP 400.
const attachmentNameKey = (name: string): string =>
  name.trim().replace(/\s+/g, " ").toLowerCase();

// Returns a filename that does not collide with any name whose key is in
// `takenKeys`. On collision, inserts a `(YYYY-MM-DD)` suffix before the
// extension; if that name is also taken, appends an incrementing counter.
const dedupeFilename = (filename: string, takenKeys: Set<string>): string => {
  if (!takenKeys.has(attachmentNameKey(filename))) {
    return filename;
  }
  const { base, ext } = splitFilename(filename);
  const date = new Date().toISOString().split("T")[0];
  let candidate = `${base} (${date})${ext}`;
  let counter = 2;
  while (takenKeys.has(attachmentNameKey(candidate))) {
    candidate = `${base} (${date}) (${counter})${ext}`;
    counter += 1;
  }
  return candidate;
};

type Source = client.AttachmentInfo["attachments"][number]["source"];
const sourceDescription = (
  source: Source
): { icon: string; description: string } => {
  switch (source) {
    case "email":
      return { icon: "pi pi-inbox", description: "Email" };
    case "screenshot_tool":
      return { icon: "pi pi-camera", description: "Polaris Screenshot" };
    case "generated":
      return {
        icon: "pi pi-file-export",
        description: "Generated from a policy application",
      };
    case null:
    case undefined:
    case "user_upload":
      return {
        icon: "pi pi-user-edit",
        description: "User uploaded attachment",
      };
  }
};

const classificationFromAttachment = (attachment: client.Attachment) => {
  return attachmentsUiTypeMap[effectiveMapping(attachment)];
};
type MappedAttachment =
  | {
      name: string;
      classification: string;
      isUserEdited: boolean;
      original: AttachmentType;
      fromEmail: boolean;
      isPending: boolean;
      source: Source | undefined;
      // ISO timestamp for files uploaded through the app; null for email and
      // legacy attachments, which have no recorded upload time.
      uploadedAt: string | null;
    }
  | {
      name: string;
      classification: string | undefined;
      isUserEdited: false;
      original: undefined;
      fromEmail: false;
      isPending: boolean;
      source: Source | undefined;
      uploadedAt: string | null;
    };

function formatUploadDate(uploadedAt: string | null): string {
  const date = parseUploadedAt(uploadedAt);
  if (!date) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Full date + time (with time zone) shown on hover, since the cell only shows
// the day.
function formatUploadTimestamp(uploadedAt: string | null): string {
  const date = parseUploadedAt(uploadedAt);
  if (!date) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
}

const priorityValues = [
  "Rater",
  "Allied Policy",
  "Acord App",
  "Loss Run",
  "Sov Spreadsheet",
  "Bound SOV",
  "Policy Document",
  "Luminor Quote",
  "Luminor TRIA",
  "Luminor Binder",
  "Luminor Policy",
  "Lead Quote",
  "Lead Binder",
  "Lead Policy",
  "SL Letter",
  "Specs Sheet",
  "Loss Control Report",
  "Record Keeping",
  "Image",
  "None",
];

export const sortAttachments = (attachments: client.Attachment[]) => {
  attachments.sort((item1, item2) => {
    const value1 = classificationFromAttachment(item1);
    const value2 = classificationFromAttachment(item2);

    // Check if values are in priority list
    const priority1 = priorityValues.indexOf(value1);
    const priority2 = priorityValues.indexOf(value2);

    // Both values are in priority list
    if (priority1 >= 0 && priority2 >= 0) {
      // Sort by priority index
      return priority1 - priority2;
    }
    // Only value1 is in priority list
    else if (priority1 >= 0) {
      return -1;
    }
    // Only value2 is in priority list
    else if (priority2 >= 0) {
      return 1;
    }
    // Neither value is in priority list - sort alphabetically
    else {
      return value1.localeCompare(value2);
    }
  });
};

export const AttachmentCard: React.FC<AttachmentCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const toast = useToast();
  const clientId = useClientId();
  const isLightMode = useContext(ThemeContext).theme === "light";
  const originalAttachmentInfo = cloneDeep(
    specialtyPropertyInfo.report_json.attachment_info ?? { attachments: [] }
  );
  // Sort before use state so that it doesn't change unless a save happens
  sortAttachments(originalAttachmentInfo.attachments);

  const [attachmentInfo, setAttachmentInfo] = useState(originalAttachmentInfo);

  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);
  const deleteAttachment = useDeleteAttachment(specialtyPropertyInfo.report_id);
  const [isEditing, setIsEditing] = useState(false);
  const [showRelevantOnly, setShowRelevantOnly] = useState(false);
  const tooltipId = `tooltip-${uuid()}`;
  const [downloadingNames, setDownloadingNames] = useState<string[]>([]);
  const [deletingNames, setDeletingNames] = useState<string[]>([]);
  const [progress, setProgress] = useState("");
  const [isPreviewingEml, setIsPreviewingEml] = useState(false);
  const [isDownloadingEml, setIsDownloadingEml] = useState(false);
  const [emlPreview, setEmlPreview] = useState<
    { subject: string; body: string } | undefined
  >(undefined);

  // Document preview state
  const [previewFile, setPreviewFile] = useState<{
    filename: string;
    fromEmail: boolean;
  } | null>(null);

  const fetchPreviewBlob = useCallback(() => {
    if (!previewFile) return Promise.reject(new Error("No file selected"));
    return previewFile.fromEmail
      ? downloadReportFile(
          specialtyPropertyInfo.report_id,
          previewFile.filename
        )
      : downloadAdditionalFile(
          specialtyPropertyInfo.report_id,
          previewFile.filename
        );
  }, [previewFile, specialtyPropertyInfo.report_id]);

  const uploadFile = useUploadAdditionalFiles(
    specialtyPropertyInfo.report_id,
    (e) => {
      setProgress(
        e.progress
          ? e.progress < 1
            ? `Uploading... ${Math.round(e.progress * 100)}%`
            : "Processing new files..."
          : ""
      );
    },
    false /* isScreenshot */
  );

  const additionalFiles = useAdditionalFiles(
    specialtyPropertyInfo.report_id,
    true
  );
  const additionalFileList = additionalFiles.data?.files ?? [];
  const additionalFileNames = additionalFileList.map((f) => f.name);
  const uploadedAtByName: Record<string, string | null> = Object.fromEntries(
    additionalFileList.map((f) => [f.name, f.uploaded_at])
  );
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const initialFiles =
    specialtyPropertyInfo.report_json.additional_files_used ?? [];

  const mappedAttachments: MappedAttachment[] = pendingFiles
    .filter((f) => attachmentInfo.attachments.every((a) => a.name !== f.name))
    .map(
      (f): MappedAttachment => ({
        name: f.name,
        classification: undefined,
        fromEmail: false,
        isPending: true,
        isUserEdited: false,
        original: undefined,
        source: undefined,
        uploadedAt: null,
      })
    )
    .concat(
      additionalFileNames
        .filter((f) => attachmentInfo.attachments.every((a) => a.name !== f))
        .map((name) => ({
          name,
          classification: undefined,
          isUserEdited: false,
          original: undefined,
          fromEmail: false,
          isPending: false,
          source: "user_upload",
          uploadedAt: uploadedAtByName[name] ?? null,
        }))
    )
    .concat(
      attachmentInfo.attachments.map((attachment) => {
        const info = originalAttachmentInfo.attachments.find(
          (a) => a.name == attachment.name
        );
        return {
          name: attachment.name,
          classification: classificationFromAttachment(attachment),
          isUserEdited: attachment.manual_mapping !== null,
          original: attachment.original_mapping,
          fromEmail: info?.from_email ?? false,
          isPending:
            !info &&
            !additionalFileNames.some((name) => name === attachment.name),
          source: info?.source,
          uploadedAt: uploadedAtByName[attachment.name] ?? null,
        };
      })
    );

  const initialFilenameOrder = originalAttachmentInfo.attachments.map(
    (a) => a.name
  );
  mappedAttachments.sort((a, b) => {
    const aIndex = initialFilenameOrder.indexOf(a.name);
    const bIndex = initialFilenameOrder.indexOf(b.name);
    if (aIndex > -1 && bIndex > -1) {
      return aIndex - bIndex;
    } else if (aIndex === -1 && bIndex === -1) {
      return a.name.localeCompare(b.name);
    } else {
      return aIndex - bIndex;
    }
  });

  const relevanceByName = computeAttachmentRelevance(
    attachmentInfo.attachments,
    {
      sovs: specialtyPropertyInfo.report_json.sovs,
      lossRun: specialtyPropertyInfo.report_json.loss_run,
      selectedPolicyFormName:
        specialtyPropertyInfo.report_json.policy_info
          ?.selected_policy_form_name ?? null,
      uploadedAtByName,
    }
  );

  const recordKeepingEmls = mappedAttachments.filter(
    (a) =>
      a.name.toLowerCase().endsWith(".eml") &&
      a.classification === "Record Keeping"
  );
  // The synthetic row that stands in for several collapsed record-keeping
  // .eml files. Its name is not a real attachment, so it is not a stable
  // comment anchor and gets no comment thread (see the Comments column).
  const collapsedRecordKeepingName = `${recordKeepingEmls.length} email thread files`;
  const displayAttachments =
    isEditing || recordKeepingEmls.length <= 1
      ? mappedAttachments
      : mappedAttachments
          .filter(
            (a) =>
              !(
                a.name.toLowerCase().endsWith(".eml") &&
                a.classification === "Record Keeping"
              )
          )
          .concat({
            name: collapsedRecordKeepingName,
            classification: "Record Keeping",
            isUserEdited: false,
            original: "record_keeping" as AttachmentType,
            fromEmail: false,
            isPending: false,
            source: undefined,
            uploadedAt: null,
          });

  // The relevance filter is view-only; while editing every row must stay
  // visible so unclassified files can be assigned a classification.
  const visibleAttachments =
    showRelevantOnly && !isEditing
      ? displayAttachments.filter((a) => relevanceByName.has(a.name))
      : displayAttachments;

  const hasUnclassifiedAttachments = mappedAttachments.some(
    (a) => a.classification === undefined
  );

  const isRerunningRemotely = useThisReportIsUpdating(
    specialtyPropertyInfo.report_id
  );
  const rerunningMessage = useRerunningMessage({
    update: isRerunningRemotely?.updating,
  });

  const { attachmentsEnabled, attachmentsDisabledText, buttonTooltip } =
    (() => {
      if (isRerunningRemotely?.updating) {
        return {
          attachmentsEnabled: false,
          attachmentsDisabledText: "",
          buttonTooltip: rerunningMessage,
        };
      }

      return { attachmentsEnabled: true, attachmentsDisabledText: "" };
    })();

  return (
    <Card
      header={
        <div
          key={isEditing ? "editing" : "not-editing"}
          className={styles.horizontalContainer}
          style={{
            paddingTop: "20px",
            paddingLeft: "20px",
          }}
        >
          <span className="p-card-title" style={{ marginRight: "12px" }}>
            Attachments
          </span>
          {!isEditing && (
            <>
              <Button
                disabled={updateReport.isPending || !attachmentsEnabled}
                label="Edit"
                onClick={() => setIsEditing(true)}
                icon="pi pi-pencil"
                raised
                severity="secondary"
                text
                tooltip={buttonTooltip}
                tooltipOptions={{ showDelay: 500, showOnDisabled: true }}
              />
              <FileUpload
                auto
                disabled={!attachmentsEnabled}
                mode="basic"
                multiple
                customUpload
                chooseLabel={"Add additional files to this report"}
                uploadHandler={(e) => {
                  const takenKeys = new Set<string>([
                    ...additionalFileNames.map(attachmentNameKey),
                    ...attachmentInfo.attachments.map((a) =>
                      attachmentNameKey(a.name)
                    ),
                  ]);
                  const renamed: string[] = [];
                  const files = e.files.map((file) => {
                    const newName = dedupeFilename(file.name, takenKeys);
                    takenKeys.add(attachmentNameKey(newName));
                    if (newName === file.name) {
                      return file;
                    }
                    renamed.push(newName);
                    return new File([file], newName, {
                      type: file.type,
                      lastModified: file.lastModified,
                    });
                  });
                  if (renamed.length > 0) {
                    toast.show({
                      severity: "info",
                      summary:
                        renamed.length === 1
                          ? "File renamed to avoid a name conflict"
                          : "Files renamed to avoid name conflicts",
                      detail: renamed.join(", "),
                    });
                  }
                  setPendingFiles(files);
                  setIsEditing(true);
                  e.options.clear();
                }}
                chooseOptions={{ className: "p-button-text p-button-raised" }}
              />
              <Button
                label="Relevant only"
                icon={showRelevantOnly ? "pi pi-star-fill" : "pi pi-star"}
                onClick={() => setShowRelevantOnly((value) => !value)}
                raised
                severity={showRelevantOnly ? "warning" : "secondary"}
                text
                tooltip="Show only the attachments this report is using: the enabled SOV and loss runs, the selected policy form, and the latest file of each other classification"
                tooltipOptions={{ showDelay: 500 }}
              />
              <Button
                label="Show email"
                onClick={async () => {
                  try {
                    setIsPreviewingEml(true);
                    const { subject, eml: body } = await previewEml(
                      specialtyPropertyInfo.report_id
                    );
                    setEmlPreview({ subject, body });
                  } finally {
                    setIsPreviewingEml(false);
                  }
                }}
                disabled={isPreviewingEml}
                loading={isPreviewingEml}
                raised
                severity="secondary"
                text
              />
              <EmlPreviewModal
                data={emlPreview}
                onHide={() => setEmlPreview(undefined)}
              />
              <Button
                label="Download email"
                onClick={async () => {
                  const id = specialtyPropertyInfo.report_id;
                  let name =
                    specialtyPropertyInfo.report_json.company_info.company_name;
                  if (name.endsWith(".")) {
                    name = name.substring(0, name.length - 1);
                  }
                  try {
                    setIsDownloadingEml(true);
                    downloadBlob(
                      `${name}.eml`,
                      await downloadReportFile(id, `${id}.eml`)
                    );
                  } finally {
                    setIsDownloadingEml(false);
                  }
                }}
                disabled={isDownloadingEml}
                loading={isDownloadingEml}
                raised
                severity="secondary"
                text
              />
            </>
          )}
          {isEditing && (
            <>
              <Button
                label="Cancel"
                disabled={updateReport.isPending}
                onClick={() => {
                  setAttachmentInfo(originalAttachmentInfo);
                  setIsEditing(false);
                  setPendingFiles([]);
                }}
                raised
                severity="secondary"
                text
              />
              <Button
                loading={updateReport.isPending || uploadFile.isPending}
                disabled={
                  hasUnclassifiedAttachments ||
                  (isEqual(originalAttachmentInfo, attachmentInfo) &&
                    isEqual(
                      initialFiles.toSorted(),
                      additionalFileNames.toSorted()
                    ))
                }
                label={progress || "Save and Reprocess"}
                onClick={async () => {
                  try {
                    if (pendingFiles.length > 0) {
                      await uploadFile.mutateAsync(pendingFiles);
                    }
                    setProgress("Re-running");
                    await updateReport.mutateAsync({
                      attachmentInfo: attachmentInfo,
                      rerunReport: true,
                    });
                    setIsEditing(false);
                  } catch (err) {
                    toast.show({
                      severity: "error",
                      summary:
                        err instanceof FileTooLargeError
                          ? "File too large"
                          : "Error re-processing report",
                      detail:
                        err instanceof FileTooLargeError
                          ? err.message
                          : (apiErrorDetail(err) ?? "Please try again."),
                      sticky: true,
                    });
                  } finally {
                    setProgress("");
                  }
                }}
                raised
                text
              />
            </>
          )}
        </div>
      }
    >
      <div className={styles.verticalContainer}>
        {attachmentsDisabledText && (
          <div>
            <Message severity="warn" text={attachmentsDisabledText} />
          </div>
        )}
        <div className="pantheon-small-table">
          {displayAttachments.length === 0 ? (
            <span>No attachments found</span>
          ) : (
            <DataTable
              value={visibleAttachments}
              removableSort
              emptyMessage="No relevant attachments"
              rowClassName={(data: MappedAttachment) =>
                relevanceByName.has(data.name) ? styles.relevantRow : ""
              }
            >
              <Column
                headerStyle={{ width: "2.5rem" }}
                body={(data: MappedAttachment) => {
                  const reason = relevanceByName.get(data.name);
                  if (!reason) return null;
                  return (
                    <TooltipWrapper
                      tooltipText={reason}
                      showDelay={300}
                      tooltipProps={{ position: "top" }}
                    >
                      <span
                        className="pi pi-star-fill"
                        style={{ color: "#f59e0b" }}
                      />
                    </TooltipWrapper>
                  );
                }}
              />
              <Column
                headerStyle={{
                  height: "44px",
                }}
                header="Actions"
                body={(data: MappedAttachment) => {
                  const isRollup = data.name.endsWith("email thread files");
                  if (isRollup) return null;
                  const isDownloading = downloadingNames.includes(data.name);
                  const previewable = canPreview(data.name) && !data.isPending;
                  const isDeleting = deletingNames.includes(data.name);
                  // Only user-uploaded attachments tracked in attachment_info
                  // can be unlinked; email attachments and not-yet-saved files
                  // cannot. Hidden while editing so it can't conflict with an
                  // in-progress classification save.
                  const deletable =
                    !isEditing &&
                    !data.fromEmail &&
                    !data.isPending &&
                    data.original !== undefined;
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      <Button
                        size="small"
                        text
                        disabled={data.isPending || isDownloading}
                        icon={
                          isDownloading
                            ? "pi pi-spin pi-spinner"
                            : "pi pi-download"
                        }
                        onClick={async () => {
                          setDownloadingNames((names) =>
                            names.concat(data.name)
                          );
                          try {
                            downloadBlob(
                              data.name,
                              data.fromEmail
                                ? await downloadReportFile(
                                    specialtyPropertyInfo.report_id,
                                    data.name
                                  )
                                : await downloadAdditionalFile(
                                    specialtyPropertyInfo.report_id,
                                    data.name
                                  )
                            );
                          } finally {
                            setDownloadingNames((names) =>
                              names.filter((name) => name !== data.name)
                            );
                          }
                        }}
                        tooltip="Download"
                        tooltipOptions={{ showDelay: 400 }}
                      />
                      {previewable && (
                        <Button
                          size="small"
                          text
                          icon="pi pi-eye"
                          onClick={() =>
                            setPreviewFile({
                              filename: data.name,
                              fromEmail: data.fromEmail,
                            })
                          }
                          tooltip="Preview"
                          tooltipOptions={{ showDelay: 400 }}
                        />
                      )}
                      {deletable && (
                        <Button
                          size="small"
                          text
                          severity="danger"
                          disabled={isDeleting}
                          icon={
                            isDeleting ? "pi pi-spin pi-spinner" : "pi pi-trash"
                          }
                          onClick={() =>
                            confirmDialog({
                              message: `Remove "${data.name}" from this report? Any parts of the report it impacted will be reprocessed.`,
                              header: "Delete attachment",
                              acceptClassName: "p-button-danger",
                              accept: async () => {
                                setDeletingNames((names) =>
                                  names.concat(data.name)
                                );
                                try {
                                  await deleteAttachment.mutateAsync(data.name);
                                  // Drop the row from local state so it
                                  // disappears immediately; the table renders
                                  // from this state, not the prop.
                                  setAttachmentInfo((prev) => ({
                                    ...prev,
                                    attachments: prev.attachments.filter(
                                      (a) => a.name !== data.name
                                    ),
                                  }));
                                } catch {
                                  toast.show({
                                    severity: "error",
                                    summary: "Error deleting attachment",
                                    detail: "Please try again.",
                                    sticky: true,
                                  });
                                } finally {
                                  setDeletingNames((names) =>
                                    names.filter((n) => n !== data.name)
                                  );
                                }
                              },
                            })
                          }
                          tooltip="Delete"
                          tooltipOptions={{ showDelay: 400 }}
                        />
                      )}
                    </div>
                  );
                }}
              />
              <Column
                header="Filename"
                field="name"
                sortable
                body={(data) => (
                  <div className={styles.horizontalContainer}>
                    <span className={iconForFilename(data.name)} />
                    <span style={{ marginTop: "4px" }}>{data.name}</span>
                  </div>
                )}
              />
              <Column
                header="Classification"
                field="classification"
                body={(data: MappedAttachment) => {
                  if (isEditing) {
                    const additionalStyles =
                      data.classification === undefined
                        ? {
                            boxShadow: `0 0 0 2px ${flaggedHighlightColor(
                              100,
                              isLightMode,
                              false
                            )}`,
                          }
                        : {};
                    return (
                      <EnumEditor
                        enumValues={validFileTypesForFilename(
                          data.name,
                          clientId
                        ).map((t) => attachmentsUiTypeMap[t])}
                        disabled={updateReport.isPending}
                        options={{
                          value: data.classification,
                          editorCallback: (newValue) => {
                            setAttachmentInfo((oldAttachmentInfo) => {
                              const newAttachmentInfo =
                                cloneDeep(oldAttachmentInfo);
                              const attachmentIndex =
                                newAttachmentInfo.attachments.findIndex(
                                  (attachment) => attachment.name === data.name
                                );
                              const newAttachmentType =
                                attachmentTypeFromString(newValue);
                              if (newAttachmentType) {
                                if (attachmentIndex > -1) {
                                  const attachment =
                                    newAttachmentInfo.attachments[
                                      attachmentIndex
                                    ]!;
                                  newAttachmentInfo.attachments[
                                    attachmentIndex
                                  ] = {
                                    ...attachment,
                                    manual_mapping:
                                      attachment.original_mapping ===
                                      newAttachmentType
                                        ? null
                                        : newAttachmentType,
                                  };
                                } else {
                                  // not found, need to add a new record.
                                  newAttachmentInfo.attachments.push({
                                    name: data.name,
                                    from_email: data.fromEmail,
                                    manual_mapping: newAttachmentType,
                                    original_mapping: newAttachmentType, // ?
                                    source: "user_upload",
                                  });
                                }
                              }

                              return newAttachmentInfo;
                            });
                          },
                        }}
                        additionalStyles={{
                          ...additionalStyles,
                          width: "200px",
                        }}
                        scrollHeight="400px"
                        placeholder="Required"
                      />
                    );
                  }

                  const hasHighlight =
                    data.isUserEdited || data.classification === undefined;

                  const flagText = data.fromEmail
                    ? data.classification && data.original
                      ? `This classification was set by a user; originally, the system set it to "${
                          attachmentsUiTypeMap[data.original]
                        }"`
                      : `This file has not been classified yet. Use the edit button above to choose one and save the report.`
                    : `This classification was set by a user`;

                  return (
                    <div style={{ width: "200px" }}>
                      <span
                        id={tooltipId}
                        style={
                          hasHighlight
                            ? {
                                backgroundColor: flaggedHighlightColor(
                                  0,
                                  isLightMode,
                                  true
                                ),
                                padding: "2px",
                                width: "auto",
                                height: "auto",
                              }
                            : {}
                        }
                        data-pr-tooltip={hasHighlight ? flagText : undefined}
                        data-pr-showdelay={500}
                      >
                        {data.classification ?? "Not yet classified"}
                      </span>
                    </div>
                  );
                }}
              />
              <Column
                header="Source"
                field="source"
                body={(data: MappedAttachment) => {
                  const { icon, description } = sourceDescription(data.source);
                  return (
                    <div className={styles.horizontalContainer}>
                      <span
                        className={
                          data.isPending ? "pi pi-file-arrow-up" : icon
                        }
                      />
                      <span style={{ marginTop: "4px" }}>
                        {description}
                        {data.isPending ? " (Not yet uploaded)" : ""}
                      </span>
                    </div>
                  );
                }}
              />
              <Column
                header="Uploaded"
                field="uploadedAt"
                sortable
                body={(data: MappedAttachment) => {
                  const day = formatUploadDate(data.uploadedAt);
                  if (!day) return null;
                  return (
                    <TooltipWrapper
                      tooltipText={formatUploadTimestamp(data.uploadedAt)}
                      showDelay={300}
                      tooltipProps={{ position: "top" }}
                    >
                      {day}
                    </TooltipWrapper>
                  );
                }}
              />
              <Column
                header="Comments"
                align="center"
                body={(data: MappedAttachment) =>
                  data.isPending ||
                  data.name === collapsedRecordKeepingName ? null : (
                    <CommentContainer
                      reportId={specialtyPropertyInfo.report_id}
                      reportName={
                        specialtyPropertyInfo.report_json.company_info
                          .company_name
                      }
                      scope={{ kind: "attachment", attachmentName: data.name }}
                    />
                  )
                }
              />
            </DataTable>
          )}
        </div>
      </div>
      <Tooltip
        target={`#${tooltipId}`}
        position="left"
        style={{ maxWidth: "600px" }}
      />

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        visible={previewFile !== null}
        onHide={() => setPreviewFile(null)}
        filename={previewFile?.filename ?? ""}
        fetchBlob={fetchPreviewBlob}
      />
    </Card>
  );
};

const EmlPreviewModal = ({
  data,
  onHide,
}: {
  data: { subject: string; body: string } | undefined;
  onHide: () => void;
}) => {
  return (
    <Dialog
      visible={!!data}
      onHide={onHide}
      header={data?.subject}
      draggable={false}
    >
      <div style={{ maxWidth: "80vw" }}>
        <Markdown
          // Don't show images or clickable links as this is user controlled input
          disallowedElements={["img", "a"]}
          unwrapDisallowed
        >
          {data?.body ?? ""}
        </Markdown>
      </div>
    </Dialog>
  );
};
